// P3-009: Board core operations — swap, resolve, cascade, gravity, reshuffle

import type { Tile, TilePosition, CascadeStep, CascadeResult } from '../types/board';
import { BOARD_COLS, BOARD_ROWS } from '../data/board-constants';
import { detectAll, hasAnyMatch, hasValidMove, getUniquePositions, wouldProduceMatch } from './MatchDetector';
import { useBoardStore } from '../stores/boardStore';
import { shuffle } from '@/shared/utils/random';

const MAX_CASCADE_DEPTH = 50; // Safety limit to prevent infinite loops

/**
 * Validate that two positions are adjacent (up/down/left/right, not diagonal).
 */
export function areAdjacent(pos1: TilePosition, pos2: TilePosition): boolean {
  const dx = Math.abs(pos1.x - pos2.x);
  const dy = Math.abs(pos1.y - pos2.y);
  return (dx === 1 && dy === 0) || (dx === 0 && dy === 1);
}

/**
 * Check if a swap would be valid WITHOUT modifying any state.
 * Used for pre-validation before committing turn state changes.
 */
export function wouldBeValidSwap(pos1: TilePosition, pos2: TilePosition): boolean {
  const store = useBoardStore.getState();
  const { tiles } = store;

  if (!areAdjacent(pos1, pos2)) return false;

  const tile1 = tiles[pos1.x]?.[pos1.y];
  const tile2 = tiles[pos2.x]?.[pos2.y];
  if (!tile1 || !tile2) return false;
  if (tile1.locked || tile2.locked) return false;
  if (!wouldProduceMatch(tiles, pos1, pos2)) return false;

  return true;
}

/**
 * Try to swap two tiles.
 * Returns false if the swap is invalid (not adjacent, locked, or doesn't produce a match).
 */
export function trySwap(pos1: TilePosition, pos2: TilePosition): boolean {
  const store = useBoardStore.getState();
  const { tiles } = store;

  // Validate adjacency
  if (!areAdjacent(pos1, pos2)) return false;

  // Check bounds
  const tile1 = tiles[pos1.x]?.[pos1.y];
  const tile2 = tiles[pos2.x]?.[pos2.y];
  if (!tile1 || !tile2) return false;

  // Check locked
  if (tile1.locked || tile2.locked) return false;

  // Check if swap would produce a match
  if (!wouldProduceMatch(tiles, pos1, pos2)) return false;

  // Perform the swap
  store.swapTiles(pos1, pos2);
  return true;
}

/**
 * Resolve all matches on the current board.
 * Returns the complete cascade chain.
 *
 * Flow: detect matches -> remove -> gravity -> detect again -> repeat
 */
export function resolveMatches(): CascadeResult {
  const steps: CascadeStep[] = [];
  let totalMatches = 0;
  let totalTilesRemoved = 0;
  let depth = 0;

  while (depth < MAX_CASCADE_DEPTH) {
    const store = useBoardStore.getState();
    const matches = detectAll(store.tiles);

    if (matches.length === 0) break;

    const positions = getUniquePositions(matches);

    totalMatches += matches.length;
    totalTilesRemoved += positions.length;

    // Remove matched tiles
    store.removeTiles(positions);

    // Apply gravity and fill
    store.applyGravity();

    const snapshot = useBoardStore.getState().tiles.map(col =>
      col.map(tile => tile ? { ...tile } : null)
    );
    steps.push({
      matches,
      removedPositions: positions,
      boardAfterGravity: snapshot,
    });

    depth++;
  }

  // Check for deadlock after resolution
  const store = useBoardStore.getState();
  if (!hasAnyMatch(store.tiles) && !hasValidMove(store.tiles)) {
    reshuffleBoard();
  }

  return {
    steps,
    totalMatches,
    totalTilesRemoved,
  };
}

/**
 * Reshuffle the board when there are no valid moves.
 * Preserves locked tiles in their positions.
 */
export function reshuffleBoard(): void {
  const store = useBoardStore.getState();
  const { tiles } = store;

  // Collect all non-locked tile elements
  const elements: Tile[] = [];
  const lockedPositions: TilePosition[] = [];

  for (let x = 0; x < BOARD_COLS; x++) {
    for (let y = 0; y < BOARD_ROWS; y++) {
      const tile = tiles[x]?.[y];
      if (tile) {
        if (tile.locked) {
          lockedPositions.push({ x, y });
        } else {
          elements.push(tile);
        }
      }
    }
  }

  // Shuffle non-locked elements
  shuffle(elements);

  // Rebuild the board
  let elemIdx = 0;
  const newTiles: (Tile | null)[][] = [];
  for (let x = 0; x < BOARD_COLS; x++) {
    newTiles[x] = [];
    for (let y = 0; y < BOARD_ROWS; y++) {
      const isLocked = lockedPositions.some((p) => p.x === x && p.y === y);
      if (isLocked) {
        newTiles[x]![y] = tiles[x]?.[y] ?? null;
      } else {
        const elem = elements[elemIdx];
        if (elem) {
          newTiles[x]![y] = { ...elem, x, y, locked: false };
          elemIdx++;
        } else {
          newTiles[x]![y] = null;
        }
      }
    }
  }

  // Apply the shuffled board, or regenerate if shuffle still has no valid moves
  if (hasValidMove(newTiles)) {
    store.setTiles(newTiles);
  } else {
    store.generateBoard();
  }
}

/**
 * Perform a complete swap + resolve cycle.
 * This is the main entry point for a player action.
 * Returns null if swap is invalid, or the cascade result.
 */
export function performSwapAndResolve(
  pos1: TilePosition,
  pos2: TilePosition,
): CascadeResult | null {
  if (!trySwap(pos1, pos2)) {
    return null;
  }

  return resolveMatches();
}
